import { cx } from 'utils';

/**
 * LxIcon — LearnX's own icon set.
 *
 * One stroke weight on a 24 grid, rounded joins, and a single filled indigo
 * node on every glyph (`dot`). The node is the signature: it is what makes the
 * set read as LearnX's rather than a stock pack, so a new glyph should carry
 * one, placed on the part of the drawing that means "the thing you act on".
 *
 * The node is drawn with `fill-brand-500`, which reads on both canvases; the
 * strokes follow `currentColor`, so the icon takes the colour of its text.
 *
 *   <LxIcon name="classes" />           20px, nav size
 *   <LxIcon name="streak" size={22} />  any size; the stroke scales with it
 *   <LxIcon name="classes" dot={false}/> plain glyph, e.g. inside a filled button
 */
const GLYPHS = {
  home: {
    d: 'M4 10.5 12 4l8 6.5V19a1 1 0 0 1-1 1h-4.5v-5h-5v5H5a1 1 0 0 1-1-1z',
    dot: [12, 10.3, 1.5],
  },
  students: {
    d: 'M3.5 19.5c.8-3 3-4.6 5.5-4.6s4.7 1.6 5.5 4.6M15.8 14.9c2.1.3 3.6 1.8 4.2 4.6',
    circles: [[9, 8.5, 3.2]],
    dot: [17.2, 8.2, 1.6],
  },
  classes: {
    d: 'm16 10.5 5-3v9l-5-3',
    rects: [[3, 6, 13, 12, 3]],
    dot: [7.2, 10, 1.5],
  },
  calendar: {
    d: 'M3.5 10h17M8 3v4M16 3v4',
    rects: [[3.5, 5, 17, 15, 3]],
    dot: [15.5, 15, 1.6],
  },
  swap: {
    d: 'M5 8h13l-3.2-3.2M19 16H6l3.2 3.2',
    dot: [12, 12, 1.5],
  },
  clock: {
    d: 'M12 7.5V12l3 2',
    circles: [[12, 12, 8.5]],
    dot: [12, 12, 1.4],
  },
  slot: {
    d: 'M3.5 10h17M8 3v4M16 3v4M12 13v4.5M9.8 15.2h4.4',
    rects: [[3.5, 5, 17, 15, 3]],
    dot: [17.2, 14, 1.3],
  },
  records: {
    d: 'M9.2 4.5V3.4h5.6v1.1M8.8 13.2l2.2 2.2 4.2-4.4',
    rects: [[5, 4.5, 14, 16.5, 2.6]],
    dot: [15.6, 18, 1.2],
  },
  wallet: {
    d: 'M3.5 9V7.6A2.6 2.6 0 0 1 6.1 5H16M20.5 11.5h-3.5a2 2 0 0 0 0 4h3.5',
    rects: [[3.5, 8, 17, 11.5, 3]],
    dot: [17, 13.5, 1.3],
  },
  library: {
    d: 'M5 4.5h4v15H5zM9 4.5h4v15H9zM14.6 5.6l3.7-1 3.4 13.6-3.7 1z',
    dot: [7, 8, 1.2],
  },
  briefcase: {
    d: 'M3.5 8.5A2.5 2.5 0 0 1 6 6h3.6l2 2H18a2.5 2.5 0 0 1 2.5 2.5v7A2.5 2.5 0 0 1 18 20H6a2.5 2.5 0 0 1-2.5-2.5zM12 11.5v5M9.5 14h5',
    dot: [17.5, 11, 1.2],
  },
  review: {
    d: 'M3.5 8.5A2.5 2.5 0 0 1 6 6h3.6l2 2H18a2.5 2.5 0 0 1 2.5 2.5v7A2.5 2.5 0 0 1 18 20H6a2.5 2.5 0 0 1-2.5-2.5zM10 14l1.6 1.6 2.9-3.1',
    dot: [17.5, 11, 1.2],
  },
  chart: {
    d: 'M4 18.5c3-1 4-8 7-8s3.5 5 6 5 2.5-6 3-9M4 21h16',
    dot: [20, 6.5, 1.6],
  },
  code: {
    d: 'M8.5 8 4.5 12l4 4M15.5 8l4 4-4 4M13.2 6.5l-2.4 11',
    dot: [19.5, 5, 1.3],
  },
  flask: {
    d: 'M9.5 3.5h5M10.5 3.5v5.2l-4.9 8.7a2 2 0 0 0 1.8 3.1h9.2a2 2 0 0 0 1.8-3.1l-4.9-8.7V3.5M7.6 14.5h8.8',
    dot: [11, 17.4, 1.2],
  },
  settings: {
    d: 'M5 7h8M17 7h2M5 17h2M11 17h8',
    circles: [[15, 7, 2], [9, 17, 2]],
  },
  streak: {
    d: 'M12 3.5c.7 3.1-3.8 5-3.8 9.6a3.8 3.8 0 0 0 7.6 0c0-1.7-.9-2.9-1.7-3.8.2 1.7-.6 2.7-1.5 2.7 0-2.5 1-5.5-.6-8.5z',
    dot: [12, 16.5, 1.3],
  },
  trophy: {
    d: 'M8 4.5h8v5a4 4 0 0 1-8 0zM8 6.5H5a3 3 0 0 0 3 4M16 6.5h3a3 3 0 0 1-3 4M12 13.5v3.5M8.5 20.5h7',
    dot: [12, 8.5, 1.3],
  },
  globe: {
    d: 'M3.5 12h17M12 3.5c2.4 2.4 3.4 5.3 3.4 8.5s-1 6.1-3.4 8.5c-2.4-2.4-3.4-5.3-3.4-8.5s1-6.1 3.4-8.5z',
    circles: [[12, 12, 8.5]],
  },
  check: {
    d: 'm5 12.5 4.5 4.5L19 7.5',
  },
  bell: {
    d: 'M6 16.5V11a6 6 0 0 1 12 0v5.5l1.5 2h-15zM10 20.5a2.2 2.2 0 0 0 4 0',
    dot: [17.5, 6.5, 1.6],
  },
  flag: {
    d: 'M5.5 20.5v-16M5.5 4.5h11l-2 4 2 4h-11',
    dot: [5.5, 20.5, 1.3],
  },
  users: {
    d: 'M3.5 19.5c.8-3 3-4.6 5.5-4.6s4.7 1.6 5.5 4.6M15.8 14.9c2.1.3 3.6 1.8 4.2 4.6',
    circles: [[9, 8.5, 3.2]],
    dot: [17.2, 8.2, 1.6],
  },
  graduation: {
    d: 'M2.5 9.5 12 5l9.5 4.5L12 14zM6.5 11.5v4.2c1.5 1.3 3.4 2 5.5 2s4-.7 5.5-2v-4.2M21.5 9.5v5',
    dot: [12, 9.6, 1.3],
  },
  arrow: {
    d: 'M5 12h14M13 6l6 6-6 6',
  },
  external: {
    d: 'M14 5h5v5M19 5l-8 8M18 14v4a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h4',
  },
};

export const LX_ICON_NAMES = Object.freeze(Object.keys(GLYPHS));

export default function LxIcon({ name, size = 20, dot = true, strokeWidth = 1.6, className, ...props }) {
  const g = GLYPHS[name];
  if (!g) return null;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={cx('lx-icon shrink-0', className)}
      {...props}
    >
      {g.rects?.map(([x, y, w, h, r]) => (
        <rect key={`r${x}${y}`} x={x} y={y} width={w} height={h} rx={r} />
      ))}
      {g.circles?.map(([cx_, cy, r]) => (
        <circle key={`c${cx_}${cy}`} cx={cx_} cy={cy} r={r} />
      ))}
      {g.d && <path d={g.d} />}
      {dot && g.dot && <circle cx={g.dot[0]} cy={g.dot[1]} r={g.dot[2]} stroke="none" className="fill-brand-500" />}
    </svg>
  );
}
